// Capture command room — the Pursuit shaping home.
//
// Routed at /capture/:id. Surfaces the pursuit with Overview (hypothesis +
// go/no-go + GovCon graph), RFIs, Interactions, and Workstreams. The three
// child tabs render as sortable/filterable data tables; a row click opens a
// side Sheet. All writes round-trip through zou.
//
// No mock data — if the workspace query 404s the page shows an error state.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { ProjectDataTypes } from '@mono/proto-clients';
import { Skeleton, SpecraBadge, SpecraButton, SpecraTabBar, SpecraTabPanel, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, SearchX, Unlink } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ConvertToProposalDialog } from '../../components/Capture/ConvertToProposalDialog';
import { LifecycleNodeKind, LinkedChips, linkedNodesQueryKey } from '../../components/LinkedChips';
import { PageBody } from '../../components/layout';
import { useAuth } from '../../contexts/authContext';
import { useTablePreferences } from '../../hooks/useTablePreferences';
import { useView } from '../../lib/access/useView';
import { useWorkflowAreas } from '../../lib/access/useWorkflowAreas';
import { EntityTable } from '../Delivery/_shared/components/EntityTable';
import { EmptyState, ErrorState } from '../Delivery/_shared/components/PageStates';
import { useCreateRfiResponseProject, useListRfiResponsesForCapture, useUnlinkRfiResponseFromCapture } from '../RfiResponse/hooks';
import { AiAssistBar, AiAssistButton } from './AiAssist';
import { CaptureCompanyLinksPanel } from './CaptureCompanyLinksPanel';
import { businessPhaseLabel, CaptureLifecyclePanel, lifecycleLabel } from './CaptureLifecyclePanel';
import { CaptureProfilePanel } from './CaptureProfilePanel';
import { CaptureReviewsPanel } from './CaptureReviewsPanel';
import { CaptureTasksPanel } from './CaptureTasksPanel';
import { CaptureTeamPanel } from './CaptureTeamPanel';
import { CaptureThemesPanel } from './CaptureThemesPanel';
import { CHANNELS, formatTs, labelOf, RFI_RESPONSE_STATUSES, RFI_TYPES, SENTIMENTS, tsMs, WORKSTREAM_STATUSES, WORKSTREAMS, workstreamLabel } from './constants';
import { DocumentsPanel } from './DocumentsPanel';
import { RfiSheet, WorkstreamSheet } from './entitySheets';
import { GoNoGoPanel } from './GoNoGoPanel';
import { GraphPanel } from './GraphPanel';
import { HypothesisCard } from './HypothesisCard';
import { useCaptureWorkspace, usePursuitOpening } from './hooks';
import { IncomingCustomerContext } from './IncomingCustomerContext';
import { OutputHandoff } from './Outputs/OutputHandoff';
import { OutputsWorkspace } from './Outputs/OutputsWorkspace';
import { overviewTarget, overviewUrl } from './Overview/overviewNavigation';
import { PursuitOverview } from './Overview/PursuitOverview';
import { PastPerformanceTab } from './PastPerformance/PastPerformanceTab';
import { useLogTouchpointDeepLink } from './record/useLogTouchpointDeepLink';
import { SolutionTab } from './Solution/SolutionTab';
import { TouchpointSheet } from './TouchpointSheet';
import { useCustomerThemes } from './themeHooks';
const SENTIMENT_STYLE = {
    positive: 'bg-green-50 text-green-700',
    neutral: 'bg-gray-100 text-gray-600',
    negative: 'bg-red-50 text-red-700',
    unknown: 'bg-gray-50 text-gray-500'
};
function SentimentBadge({ value }) {
    if (!value) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    return /*#__PURE__*/ _jsx("span", {
        className: `inline-flex items-center rounded-full px-2 py-0.5 text-xs ${SENTIMENT_STYLE[value] ?? SENTIMENT_STYLE.unknown}`,
        children: labelOf(SENTIMENTS, value)
    });
}
const RFI_RESPONSE_VARIANT = {
    responded: 'success',
    not_responded: 'neutral',
    missed: 'warning',
    pending: 'info'
};
// Did we answer this RFI? Badge plus the reason when we didn't.
function RfiResponseStatus({ rfi }) {
    if (!rfi.responseStatus) return /*#__PURE__*/ _jsx("span", {
        className: "text-muted-foreground",
        children: "—"
    });
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex min-w-0 flex-col items-start gap-1",
        children: [
            /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: RFI_RESPONSE_VARIANT[rfi.responseStatus] ?? 'neutral',
                "data-testid": Testids.capture.rfiResponseStatusBadge,
                "data-status": rfi.responseStatus,
                children: labelOf(RFI_RESPONSE_STATUSES, rfi.responseStatus)
            }),
            rfi.responseReason && /*#__PURE__*/ _jsx("span", {
                className: "max-w-xs truncate text-xs text-muted-foreground",
                title: rfi.responseReason,
                children: rfi.responseReason
            })
        ]
    });
}
export const RFI_COLUMNS = [
    {
        key: 'title',
        header: 'Title',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'rfiType',
        header: 'Type',
        sortable: true,
        filter: 'select',
        filterOptions: RFI_TYPES,
        render: (r)=>labelOf(RFI_TYPES, r.rfiType)
    },
    {
        key: 'eventDate',
        header: 'Event',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.eventDate),
        render: (r)=>formatTs(r.eventDate)
    },
    {
        key: 'responseDue',
        header: 'Response due',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.responseDue),
        render: (r)=>formatTs(r.responseDue)
    },
    {
        key: 'responseStatus',
        header: 'Response',
        sortable: true,
        filter: 'select',
        filterOptions: RFI_RESPONSE_STATUSES,
        render: (r)=>/*#__PURE__*/ _jsx(RfiResponseStatus, {
                rfi: r
            })
    }
];
export const INTERACTION_COLUMNS = [
    {
        key: 'summary',
        header: 'Touchpoint',
        sortAccessor: (r)=>[
                r.title,
                r.summary
            ].filter(Boolean).join(' — '),
        sortable: true,
        filter: 'text',
        render: (r)=>/*#__PURE__*/ _jsxs("span", {
                children: [
                    r.title || r.summary || 'Untitled touchpoint',
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: r.isDraft ? 'warning' : 'success',
                        children: r.isDraft ? 'Saved draft' : 'Finished note'
                    })
                ]
            })
    },
    {
        key: 'channel',
        header: 'Channel',
        sortable: true,
        filter: 'select',
        filterOptions: CHANNELS,
        render: (r)=>labelOf(CHANNELS, r.channel)
    },
    {
        key: 'stakeholder',
        header: 'Stakeholder',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'sentiment',
        header: 'Sentiment',
        filter: 'select',
        filterOptions: SENTIMENTS,
        render: (r)=>/*#__PURE__*/ _jsx(SentimentBadge, {
                value: r.sentiment
            })
    },
    {
        key: 'interactionDate',
        header: 'Date',
        sortable: true,
        sortAccessor: (r)=>tsMs(r.interactionDate),
        render: (r)=>formatTs(r.interactionDate)
    }
];
export const WORKSTREAM_COLUMNS = [
    {
        key: 'title',
        header: 'Title',
        sortable: true,
        filter: 'text'
    },
    {
        key: 'workstreamType',
        header: 'Workstream',
        sortable: true,
        filter: 'select',
        filterOptions: WORKSTREAMS.map((w)=>({
                value: String(w.value),
                label: w.label
            })),
        render: (r)=>workstreamLabel(r.workstreamType)
    },
    {
        key: 'status',
        header: 'Status',
        sortable: true,
        filter: 'select',
        filterOptions: WORKSTREAM_STATUSES,
        render: (r)=>labelOf(WORKSTREAM_STATUSES, r.status)
    },
    {
        key: 'aiDrafted',
        header: 'AI',
        render: (r)=>r.aiDrafted ? /*#__PURE__*/ _jsx(SpecraBadge, {
                variant: "neutral",
                children: "AI"
            }) : null
    }
];
export function CaptureDetailPage() {
    const { user } = useAuth();
    const { id } = useParams();
    const navigate = useNavigate();
    const captureProjectId = id ?? '';
    const { data, isLoading, isError, error } = useCaptureWorkspace(captureProjectId);
    const writeAccess = useView({
        kind: 'capture_project',
        id: captureProjectId,
        objectType: 'delivery.programs',
        objectId: data?.summary?.zouProjectId
    }, {
        action: 'update',
        enabled: !!data?.summary?.zouProjectId
    });
    // The not-found state offers the way back only to a viewer who can see Pursuits.
    const { isLoading: areasLoading, reachable } = useWorkflowAreas();
    const canSeePursuits = !areasLoading && reachable.capture;
    const { data: opening } = usePursuitOpening(captureProjectId);
    const { data: rfiLinkData } = useListRfiResponsesForCapture(captureProjectId);
    const { data: customerThemes } = useCustomerThemes(captureProjectId);
    const createRfiResponse = useCreateRfiResponseProject();
    const unlinkRfiResponse = useUnlinkRfiResponseFromCapture();
    // One workspace correlation per page mount — groups the session's audit rows.
    const workspaceCorrelationId = useMemo(()=>crypto.randomUUID(), []);
    // GRW-16 (OD-92): /capture/{id}?logTouchpoint=1&contact={id} opens Log
    // touchpoint here too, where growth_record_shell_v1 is off. Without the
    // params nothing changes.
    const touchpointDeepLink = useLogTouchpointDeepLink({
        canEdit: writeAccess.can('update'),
        ready: !!data?.summary?.zouProjectId && !writeAccess.isLoading
    });
    const rfiPrefs = useTablePreferences('ts:capture:rfis');
    const interactionPrefs = useTablePreferences('ts:capture:interactions');
    const workstreamPrefs = useTablePreferences('ts:capture:workstreams');
    const [gateToRecord, setGateToRecord] = useState();
    const [interactionSheet, setInteractionSheet] = useState({
        open: false,
        value: null
    });
    const [workstreamSheet, setWorkstreamSheet] = useState({
        open: false,
        value: null
    });
    const [rfiSheet, setRfiSheet] = useState({
        open: false,
        value: null
    });
    const [convertOpen, setConvertOpen] = useState(false);
    const handoffCache = useQueryClient();
    useEffect(()=>{
        if (!convertOpen) void handoffCache.invalidateQueries({
            queryKey: linkedNodesQueryKey(LifecycleNodeKind.PURSUIT, captureProjectId)
        });
    }, [
        convertOpen,
        handoffCache,
        captureProjectId
    ]);
    const [activeTab, setActiveTab] = useState('overview');
    const [outputsVisited, setOutputsVisited] = useState(false);
    useEffect(()=>{
        if (activeTab === 'outputs') setOutputsVisited(true);
    }, [
        activeTab
    ]);
    const location = useLocation();
    const outputTab = new URLSearchParams(location.search).get('tab');
    const outputOwner = new URLSearchParams(location.search).get('outputOwner');
    useEffect(()=>{
        if (!outputTab) {
            setActiveTab('overview');
            return;
        }
        if (outputTab && [
            'overview',
            'rfis',
            'interactions',
            'themes',
            'workstreams',
            'documents',
            'past-performance',
            'solution',
            'outputs'
        ].includes(outputTab)) {
            setActiveTab(outputTab);
        }
    }, [
        outputTab
    ]);
    useEffect(()=>{
        if (!outputOwner || activeTab !== 'overview') return;
        const element = document.getElementById(outputOwner);
        if (element) {
            element.tabIndex = -1;
            element.focus({
                preventScroll: true
            });
            element.scrollIntoView({
                block: 'start'
            });
        }
    }, [
        outputOwner,
        activeTab,
        data
    ]);
    function selectTab(tab) {
        setActiveTab(tab);
        const next = new URLSearchParams(location.search);
        next.set('tab', tab);
        next.delete('outputOwner');
        navigate({
            pathname: location.pathname,
            search: next.toString()
        });
    }
    const overviewOrigin = useRef(0);
    const overviewOriginElement = useRef(null);
    const [targetMissing, setTargetMissing] = useState(false);
    const overviewParams = new URLSearchParams(location.search);
    const targetKind = overviewParams.get('overviewKind');
    const targetId = overviewParams.get('overviewId') ?? '';
    useEffect(()=>{
        setTargetMissing(false);
        if (!targetKind) {
            if (overviewOriginElement.current?.isConnected) {
                overviewOriginElement.current.focus({
                    preventScroll: true
                });
                overviewOriginElement.current.scrollIntoView({
                    block: 'center'
                });
            } else window.scrollTo({
                top: overviewOrigin.current
            });
            return;
        }
        setActiveTab(targetKind === 'theme' ? 'themes' : 'overview');
        if (targetKind === 'task' || targetKind === 'theme') {
            setInteractionSheet((previous)=>previous.open ? {
                    open: false,
                    value: null
                } : previous);
        }
        if (targetKind === 'interaction') {
            const row = data?.interactions.find((row)=>row.id === targetId);
            if (row) setInteractionSheet({
                open: true,
                value: row
            });
            else setTargetMissing(true);
            return ()=>setInteractionSheet({
                    open: false,
                    value: null
                });
        }
        if (targetKind === 'decision') {
            const selected = data?.goNoGoDecisions?.find((decision)=>decision.id === targetId);
            const panel = document.getElementById('pursuit-selected-decision');
            setTargetMissing(!selected);
            if (selected && panel) {
                panel.focus({
                    preventScroll: true
                });
                panel.scrollIntoView({
                    block: 'center'
                });
            }
            return;
        }
        const destination = {
            kind: targetKind,
            id: targetId
        };
        const focus = ()=>{
            const row = overviewTarget(document, destination);
            if (!row) return false;
            row.tabIndex = -1;
            row.focus({
                preventScroll: true
            });
            row.scrollIntoView({
                block: 'center'
            });
            setTargetMissing(false);
            return true;
        };
        if (focus()) return;
        const observer = new MutationObserver(()=>{
            if (focus()) {
                observer.disconnect();
                clearTimeout(timeout);
            }
        });
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
        const timeout = setTimeout(()=>{
            observer.disconnect();
            setTargetMissing(true);
        }, 10000);
        return ()=>{
            observer.disconnect();
            clearTimeout(timeout);
        };
    }, [
        targetKind,
        targetId,
        data?.interactions,
        data?.goNoGoDecisions
    ]);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 px-6 py-4",
                "data-testid": Testids.deliveryShared.loadingSkeleton,
                children: [
                    /*#__PURE__*/ _jsx(Skeleton, {
                        className: "h-8 w-1/3"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-3 gap-3",
                        children: [
                            0,
                            1,
                            2
                        ].map((i)=>/*#__PURE__*/ _jsx(Skeleton, {
                                className: "h-40"
                            }, i))
                    })
                ]
            })
        });
    }
    if (isError || !data?.summary) {
        // A missing capture project gets a human not-found with a way out —
        // never the raw backend error chain ("[not_found] get capture_project
        // …: record not found").
        const notFound = error instanceof ConnectError && (error.code === Code.NotFound || error.code === Code.PermissionDenied);
        return /*#__PURE__*/ _jsx(PageBody, {
            children: /*#__PURE__*/ _jsx("div", {
                className: "px-6 py-4",
                children: notFound || !error ? /*#__PURE__*/ _jsx(EmptyState, {
                    icon: SearchX,
                    title: "Pursuit not found",
                    description: "It may have been deleted, or you may not have access to it.",
                    action: canSeePursuits ? /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        onClick: ()=>navigate('/capture'),
                        children: "Back to Pursuits"
                    }) : undefined
                }) : /*#__PURE__*/ _jsx(ErrorState, {
                    title: "Pursuit workspace unavailable",
                    error: error
                })
            })
        });
    }
    const summary = data.summary;
    return /*#__PURE__*/ _jsxs(PageBody, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-4 px-6 py-4",
                "data-testid": Testids.capture.workspaceRoot,
                "data-capture-id": summary.captureProjectId,
                children: [
                    /*#__PURE__*/ _jsxs("header", {
                        className: "flex items-start justify-between gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("h1", {
                                        className: "text-2xl font-semibold",
                                        children: summary.name
                                    }),
                                    /*#__PURE__*/ _jsxs("p", {
                                        className: "mt-1 text-sm text-muted-foreground",
                                        children: [
                                            "Pursuit •",
                                            ' ',
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-700",
                                                children: [
                                                    "Lifecycle: ",
                                                    lifecycleLabel(summary.lifecycle),
                                                    " · Business phase:",
                                                    ' ',
                                                    businessPhaseLabel(summary.businessPhase)
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(LinkedChips, {
                                        className: "mt-2",
                                        kind: LifecycleNodeKind.PURSUIT,
                                        id: summary.captureProjectId
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                className: "shrink-0",
                                onClick: ()=>setConvertOpen(true),
                                disabled: !writeAccess.can('convert_to_proposal'),
                                "data-testid": Testids.capture.convertButton,
                                children: "Open Proposal"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(CaptureLifecyclePanel, {
                        summary: summary,
                        tasks: data.tasks ?? []
                    }, summary.captureProjectId),
                    /*#__PURE__*/ _jsx(ConvertToProposalDialog, {
                        open: convertOpen,
                        onOpenChange: setConvertOpen,
                        captureProjectId: summary.captureProjectId
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "w-full",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraTabBar, {
                                idBase: "capture-detail",
                                value: activeTab,
                                onValueChange: selectTab,
                                options: [
                                    {
                                        value: 'outputs',
                                        label: 'Outputs',
                                        testId: Testids.captureOutputs.tab
                                    },
                                    {
                                        value: 'past-performance',
                                        label: 'Past Performance',
                                        testId: Testids.pursuitPP.tab
                                    },
                                    {
                                        value: 'solution',
                                        label: 'Solution',
                                        testId: Testids.captureSolution.tab
                                    },
                                    {
                                        value: 'overview',
                                        label: 'Overview',
                                        testId: Testids.capture.tabOverview
                                    },
                                    {
                                        value: 'rfis',
                                        label: `RFIs (${(rfiLinkData?.workspaces.length ?? 0) + (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length)})`,
                                        testId: Testids.capture.tabRfis
                                    },
                                    {
                                        value: 'interactions',
                                        label: `Touchpoints (${data.interactions.length})`,
                                        testId: Testids.capture.tabInteractions
                                    },
                                    {
                                        value: 'themes',
                                        label: `Customer Themes (${customerThemes?.length ?? 0})`,
                                        testId: Testids.capture.tabThemes
                                    },
                                    {
                                        value: 'workstreams',
                                        label: `Workstreams (${data.workstreamItems.length})`,
                                        testId: Testids.capture.tabWorkstreams
                                    },
                                    {
                                        value: 'documents',
                                        label: 'Documents',
                                        testId: Testids.capture.tabDocuments
                                    }
                                ]
                            }),
                            outputTab && outputTab !== 'outputs' && new URLSearchParams(location.search).has('outputSection') && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "secondary",
                                "data-testid": Testids.captureOutputs.returnToOutputs,
                                onClick: ()=>selectTab('outputs'),
                                children: "Back to Outputs review"
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                keepMounted: true,
                                active: activeTab === 'outputs',
                                idBase: "capture-detail",
                                value: "outputs",
                                className: "mt-2",
                                children: (activeTab === 'outputs' || outputsVisited) && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(OutputsWorkspace, {
                                            active: activeTab === 'outputs',
                                            captureProjectId: captureProjectId,
                                            zouProjectId: summary.zouProjectId
                                        }, captureProjectId),
                                        /*#__PURE__*/ _jsx(OutputHandoff, {
                                            captureProjectId: summary.captureProjectId,
                                            onOpenProposal: ()=>setConvertOpen(true)
                                        }, `${summary.captureProjectId}:${location.search}`)
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                keepMounted: true,
                                active: activeTab === 'past-performance',
                                idBase: "capture-detail",
                                value: "past-performance",
                                className: "mt-2",
                                children: /*#__PURE__*/ _jsx(PastPerformanceTab, {
                                    captureProjectId: captureProjectId
                                })
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                active: activeTab === 'solution',
                                idBase: "capture-detail",
                                value: "solution",
                                className: "mt-2",
                                children: /*#__PURE__*/ _jsx(SolutionTab, {
                                    captureProjectId: captureProjectId,
                                    author: user ?? undefined,
                                    pinnedRevisionId: overviewParams.get('solutionRevision') ?? undefined,
                                    pinnedTargetId: overviewParams.get('solutionTarget') ?? undefined
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'overview',
                                idBase: "capture-detail",
                                value: "overview",
                                className: "mt-2 space-y-4",
                                children: [
                                    /*#__PURE__*/ _jsx(PursuitOverview, {
                                        captureProjectId: captureProjectId,
                                        onProfile: ()=>document.getElementById('pursuit-profile')?.scrollIntoView(),
                                        onTeam: ()=>document.getElementById('pursuit-team')?.scrollIntoView(),
                                        onOpen: (destination)=>{
                                            overviewOrigin.current = window.scrollY;
                                            overviewOriginElement.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
                                            if (destination.href) {
                                                const separator = destination.href.includes('?') ? '&' : '?';
                                                navigate(`${destination.href}${separator}returnTo=${encodeURIComponent(location.pathname + location.search)}`);
                                                return;
                                            }
                                            if (!destination.id) {
                                                document.getElementById(destination.kind === 'decision' ? 'pursuit-decisions' : 'pursuit-plan')?.scrollIntoView({
                                                    block: 'center'
                                                });
                                                return;
                                            }
                                            navigate(overviewUrl(location.pathname, destination));
                                        }
                                    }),
                                    targetKind && /*#__PURE__*/ _jsx(SpecraButton, {
                                        variant: "secondary",
                                        size: "sm",
                                        onClick: ()=>navigate(-1),
                                        children: "Back to overview"
                                    }),
                                    targetMissing && /*#__PURE__*/ _jsx(SpecraBadge, {
                                        variant: "warning",
                                        children: "The selected record is unavailable. Refresh or return to the overview."
                                    }),
                                    /*#__PURE__*/ _jsx(IncomingCustomerContext, {
                                        customerOrgId: summary.customerOrgId
                                    }),
                                    /*#__PURE__*/ _jsx(HypothesisCard, {
                                        captureProjectId: captureProjectId,
                                        summary: summary
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        id: "pursuit-decisions",
                                        children: [
                                            targetKind === 'decision' && /*#__PURE__*/ _jsxs("div", {
                                                id: "pursuit-selected-decision",
                                                tabIndex: -1,
                                                className: "space-y-2",
                                                children: [
                                                    /*#__PURE__*/ _jsx(SpecraText, {
                                                        className: "block",
                                                        children: "Selected decision record"
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraText, {
                                                        className: "block",
                                                        size: "meta",
                                                        tone: "muted",
                                                        children: "Showing the selected earlier decision below. Scoped decision history remains available."
                                                    }),
                                                    /*#__PURE__*/ _jsx(SpecraButton, {
                                                        variant: "secondary",
                                                        size: "sm",
                                                        onClick: ()=>navigate(location.pathname, {
                                                                replace: true
                                                            }),
                                                        children: "Show all decisions"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(GoNoGoPanel, {
                                                gateToRecord: gateToRecord,
                                                captureProjectId: captureProjectId,
                                                decisions: targetKind === 'decision' ? (data.goNoGoDecisions ?? []).filter((decision)=>decision.id === targetId) : data.goNoGoDecisions ?? [],
                                                tasks: data.tasks ?? [],
                                                correlationId: workspaceCorrelationId
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(CaptureReviewsPanel, {
                                        captureProjectId: captureProjectId,
                                        onRecordDecision: (id)=>setGateToRecord({
                                                id
                                            })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        id: "pursuit-profile",
                                        children: /*#__PURE__*/ _jsx(CaptureProfilePanel, {
                                            captureProjectId: captureProjectId,
                                            pursuitName: summary.name,
                                            profile: data.profile,
                                            competitionLinks: data.competitionLinks,
                                            correlationId: workspaceCorrelationId,
                                            opportunityId: opening?.opportunityId ?? '',
                                            qualificationId: opening?.qualificationId ?? ''
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        id: "pursuit-team",
                                        children: /*#__PURE__*/ _jsx(CaptureTeamPanel, {
                                            captureProjectId: captureProjectId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        id: "pursuit-companies",
                                        children: /*#__PURE__*/ _jsx(CaptureCompanyLinksPanel, {
                                            captureProjectId: captureProjectId,
                                            teamingLinks: data.teamingLinks,
                                            competitionLinks: data.competitionLinks,
                                            correlationId: workspaceCorrelationId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        id: "pursuit-plan",
                                        children: /*#__PURE__*/ _jsx(CaptureTasksPanel, {
                                            captureProjectId: captureProjectId,
                                            tasks: data.tasks ?? [],
                                            correlationId: workspaceCorrelationId
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx(AiAssistBar, {
                                        zouProjectId: summary.zouProjectId
                                    }),
                                    /*#__PURE__*/ _jsx(GraphPanel, {
                                        highlightRef: summary.customerOrgId
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'rfis',
                                idBase: "capture-detail",
                                value: "rfis",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                variant: "outline",
                                                disabled: !writeAccess.can('update'),
                                                "data-testid": Testids.capture.addRfiButton,
                                                onClick: ()=>setRfiSheet({
                                                        open: true,
                                                        value: null
                                                    }),
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "Log RFI"
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                "data-testid": Testids.capture.createRfiResponseButton,
                                                onClick: ()=>{
                                                    const title = summary.name ? `RFI — ${summary.name}` : 'RFI Response';
                                                    createRfiResponse.mutate({
                                                        title,
                                                        captureProjectId
                                                    }, {
                                                        onSuccess: (s)=>{
                                                            navigate(`/rfi-response/${s.zouProjectId || s.rfiResponseProjectId}`);
                                                        }
                                                    });
                                                },
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                disabled: createRfiResponse.isPending,
                                                children: "Create RFI Response"
                                            })
                                        ]
                                    }),
                                    (rfiLinkData?.workspaces.length ?? 0) > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-sm font-medium text-muted-foreground",
                                                children: "Response workspaces"
                                            }),
                                            /*#__PURE__*/ _jsx("ul", {
                                                className: "divide-y rounded-md border",
                                                children: (rfiLinkData?.workspaces ?? []).map((w)=>/*#__PURE__*/ _jsx("li", {
                                                        children: /*#__PURE__*/ _jsxs("div", {
                                                            className: "flex items-center justify-between px-3 py-2 hover:bg-muted/50",
                                                            children: [
                                                                /*#__PURE__*/ _jsxs("button", {
                                                                    type: "button",
                                                                    className: "flex min-w-0 flex-1 items-center justify-between text-left text-sm",
                                                                    "data-testid": Testids.capture.rfiWorkspaceRow,
                                                                    onClick: ()=>navigate(`/rfi-response/${w.zouProjectId || w.rfiResponseProjectId}`),
                                                                    children: [
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "truncate font-medium",
                                                                            children: w.title
                                                                        }),
                                                                        /*#__PURE__*/ _jsx("span", {
                                                                            className: "ml-2 shrink-0 text-muted-foreground",
                                                                            children: w.status
                                                                        })
                                                                    ]
                                                                }),
                                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                                    size: "sm",
                                                                    variant: "ghost",
                                                                    className: "shrink-0",
                                                                    "data-testid": Testids.capture.unlinkRfiWorkspaceButton,
                                                                    disabled: unlinkRfiResponse.isPending,
                                                                    onClick: ()=>unlinkRfiResponse.mutate({
                                                                            captureProjectId,
                                                                            rfiResponseProjectId: w.rfiResponseProjectId
                                                                        }),
                                                                    iconLeft: /*#__PURE__*/ _jsx(Unlink, {
                                                                        className: "h-4 w-4"
                                                                    }),
                                                                    "aria-label": "Unlink RFI Response workspace"
                                                                })
                                                            ]
                                                        })
                                                    }, w.rfiResponseProjectId))
                                            })
                                        ]
                                    }),
                                    (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length) > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-sm font-medium text-muted-foreground",
                                                children: "Tracked RFIs"
                                            }),
                                            /*#__PURE__*/ _jsx(EntityTable, {
                                                data: rfiLinkData?.legacyRfiEvents ?? data.rfiEvents,
                                                columns: RFI_COLUMNS,
                                                testid: Testids.capture.rfiTable,
                                                rowTestid: Testids.capture.rfiRow,
                                                rowKey: (r)=>r.id,
                                                onRowClick: (r)=>setRfiSheet({
                                                        open: true,
                                                        value: r
                                                    }),
                                                sort: rfiPrefs.sort,
                                                onToggleSort: rfiPrefs.toggleSort,
                                                filters: rfiPrefs.filters,
                                                onFilterChange: rfiPrefs.setFilter,
                                                emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                                    title: "No RFIs match",
                                                    description: "Try clearing the filters."
                                                })
                                            })
                                        ]
                                    }),
                                    (rfiLinkData?.workspaces.length ?? 0) === 0 && (rfiLinkData?.legacyRfiEvents.length ?? data.rfiEvents.length) === 0 && /*#__PURE__*/ _jsx(EmptyState, {
                                        title: "No RFIs yet",
                                        description: "Create a response workspace to draft an answer, or log an RFI to record whether we responded."
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'interactions',
                                idBase: "capture-detail",
                                value: "interactions",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraText, {
                                                className: "mr-auto",
                                                tone: "muted",
                                                children: [
                                                    "Pursuit: ",
                                                    summary.name
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(AiAssistButton, {
                                                zouProjectId: summary.zouProjectId,
                                                action: "summarize-interactions",
                                                label: "Summarize",
                                                prompt: "Summarize the customer interactions logged on this capture. Call out positive signals, risks, and the follow-ups we owe."
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                disabled: !writeAccess.can('update'),
                                                "data-testid": Testids.capture.addInteractionButton,
                                                onClick: ()=>setInteractionSheet({
                                                        open: true,
                                                        value: null
                                                    }),
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "Log Touchpoint"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(EntityTable, {
                                        data: data.interactions,
                                        columns: INTERACTION_COLUMNS,
                                        testid: Testids.capture.interactionTable,
                                        rowTestid: Testids.capture.interactionRow,
                                        rowKey: (r)=>r.id,
                                        onRowClick: (r)=>setInteractionSheet({
                                                open: true,
                                                value: r
                                            }),
                                        sort: interactionPrefs.sort,
                                        onToggleSort: interactionPrefs.toggleSort,
                                        filters: interactionPrefs.filters,
                                        onFilterChange: interactionPrefs.setFilter,
                                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                            title: "No touchpoints yet",
                                            description: "Log customer-shaping touchpoints — meetings, calls, emails, conferences."
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                active: activeTab === 'themes',
                                idBase: "capture-detail",
                                value: "themes",
                                className: "mt-2 space-y-3",
                                children: /*#__PURE__*/ _jsx(CaptureThemesPanel, {
                                    pinnedThemeId: targetKind === 'theme' ? targetId : undefined,
                                    pinnedVersionId: overviewParams.get('themeVersion') ?? undefined,
                                    captureProjectId: captureProjectId,
                                    correlationId: workspaceCorrelationId
                                })
                            }),
                            /*#__PURE__*/ _jsxs(SpecraTabPanel, {
                                active: activeTab === 'workstreams',
                                idBase: "capture-detail",
                                value: "workstreams",
                                className: "mt-2 space-y-3",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center justify-end gap-2",
                                        children: [
                                            /*#__PURE__*/ _jsx(AiAssistButton, {
                                                zouProjectId: summary.zouProjectId,
                                                action: "draft-workstream",
                                                label: "Draft analysis",
                                                prompt: "Help me make progress on this capture's workstreams. Suggest concrete items for customer shaping, solutioning, black hat, and teaming, prioritized for where this pursuit is now."
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                size: "sm",
                                                "data-testid": Testids.capture.addWorkstreamItemButton,
                                                onClick: ()=>setWorkstreamSheet({
                                                        open: true,
                                                        value: null
                                                    }),
                                                iconLeft: /*#__PURE__*/ _jsx(Plus, {
                                                    className: "h-4 w-4"
                                                }),
                                                children: "New item"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx(EntityTable, {
                                        data: data.workstreamItems,
                                        columns: WORKSTREAM_COLUMNS,
                                        testid: Testids.capture.workstreamTable,
                                        rowTestid: Testids.capture.workstreamItemRow,
                                        rowKey: (r)=>r.id,
                                        onRowClick: (r)=>setWorkstreamSheet({
                                                open: true,
                                                value: r
                                            }),
                                        sort: workstreamPrefs.sort,
                                        onToggleSort: workstreamPrefs.toggleSort,
                                        filters: workstreamPrefs.filters,
                                        onFilterChange: workstreamPrefs.setFilter,
                                        emptyState: /*#__PURE__*/ _jsx(EmptyState, {
                                            title: "No workstream items",
                                            description: "Capture structured work across the 8 capture workstreams."
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraTabPanel, {
                                active: activeTab === 'documents',
                                idBase: "capture-detail",
                                value: "documents",
                                className: "mt-2 space-y-3",
                                children: /*#__PURE__*/ _jsx(DocumentsPanel, {
                                    entityType: "capture_project",
                                    entityId: captureProjectId,
                                    title: "Pursuit documents"
                                })
                            })
                        ]
                    })
                ]
            }),
            interactionSheet.open && /*#__PURE__*/ _jsx(TouchpointSheet, {
                onClose: ()=>setInteractionSheet((s)=>({
                            ...s,
                            open: false
                        })),
                pursuitName: summary.name,
                zouProjectId: summary.zouProjectId,
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                interaction: interactionSheet.value
            }),
            !interactionSheet.open && touchpointDeepLink.open && /*#__PURE__*/ _jsx(TouchpointSheet, {
                onClose: touchpointDeepLink.close,
                pursuitName: summary.name,
                zouProjectId: summary.zouProjectId,
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                interaction: null,
                initialParticipants: touchpointDeepLink.initialParticipants
            }),
            /*#__PURE__*/ _jsx(WorkstreamSheet, {
                open: workstreamSheet.open,
                onOpenChange: (open)=>setWorkstreamSheet((s)=>({
                            ...s,
                            open
                        })),
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                item: workstreamSheet.value,
                workstreamType: ProjectDataTypes.CaptureWorkstreamType.CUSTOMER_SHAPING
            }),
            /*#__PURE__*/ _jsx(RfiSheet, {
                open: rfiSheet.open,
                onOpenChange: (open)=>setRfiSheet((s)=>({
                            ...s,
                            open
                        })),
                captureProjectId: captureProjectId,
                correlationId: workspaceCorrelationId,
                rfi: rfiSheet.value
            })
        ]
    });
}
